/**
 * What you (and your team) follow: hydrated from the follows the Worker inlines with every dashboard
 * page (snapshot:me), changed optimistically through /api/dashboard/follows.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { Follow } from "../types";
import { toastStore } from "./toastStore";
import { getViewer, signInToAct } from "../app/viewer";
import { trackFollow } from "../lib/analytics";

interface FollowsState {
  follows: Follow[];
  hydrate: (f: Follow[]) => void;
  follow: (f: Pick<Follow, "type" | "label"> & { refId?: string; keyword?: string; scope?: Follow["scope"] }) => Promise<Follow | null>;
  unfollow: (id: string) => Promise<void>;
}

export const followsStore = createStore<FollowsState>()((set, get) => ({
  follows: [],
  hydrate: (follows) => set({ follows }),
  follow: async (f) => {
    if (!getViewer().admitted) { trackFollow(true, f.type, f.refId, getViewer().signedIn); signInToAct(); return null; } // the public meeting page: sign in first
    const res = await fetch("/api/dashboard/follows", { method: "POST", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ scope: "personal", ...f }) }).catch(() => null);
    if (!res?.ok) { toastStore.getState().show({ message: "Couldn't follow that. Try again." }); return null; }
    const saved = (await res.json()) as Follow;
    set({ follows: [...get().follows, saved] });
    trackFollow(true, saved.type ?? f.type, saved.refId ?? f.refId, true);
    toastStore.getState().show({
      message: "Following. New matches will show up in Following.",
      actionLabel: "Undo",
      onAction: () => void get().unfollow(saved.id),
    });
    return saved;
  },
  unfollow: async (id) => {
    const prev = get().follows;
    set({ follows: prev.filter((f) => f.id !== id) });
    const res = await fetch(`/api/dashboard/follows/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" }).catch(() => null);
    if (!res?.ok) { set({ follows: prev }); toastStore.getState().show({ message: "Couldn't unfollow that. Try again." }); return; }
    const gone = prev.find((f) => f.id === id);
    if (gone) trackFollow(false, gone.type, gone.refId, true);
  },
}));

export const useFollows = <T,>(selector: (s: FollowsState) => T) => useStore(followsStore, selector);
export const findFollow = (follows: Follow[], type: Follow["type"], refId: string) => follows.find((f) => f.type === type && f.refId === refId);
